import {
  CloudDownloadOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useEffect, useMemo, useState } from 'react';
import { AppLayout } from '../components/AppLayout';
import { PageHead } from '../components/PageHead';
import {
  api,
  apiDownload,
  type EventRow,
  type StreamPlatform,
  type StreamingDestinationRow,
} from '../lib/api';

const { Title, Text, Paragraph } = Typography;

const PLATFORMS: { value: StreamPlatform; label: string; color: string }[] = [
  { value: 'youtube', label: 'YouTube Live', color: 'red' },
  { value: 'vimeo', label: 'Vimeo', color: 'cyan' },
  { value: 'facebook', label: 'Facebook Live', color: 'blue' },
  { value: 'twitch', label: 'Twitch', color: 'purple' },
  { value: 'linkedin', label: 'LinkedIn Live', color: 'geekblue' },
  { value: 'custom', label: 'Custom RTMP', color: 'default' },
];

type FormValues = {
  name: string;
  platform: StreamPlatform;
  event_id: string | null;
  rtmp_url: string;
  stream_key: string;
  backup_url?: string | null;
  notes?: string | null;
};

async function copy(text: string, label: string) {
  await navigator.clipboard.writeText(text);
  message.success(`Copied ${label}`);
}

function CopyField({ value, label }: { value: string | null | undefined; label: string }) {
  if (!value) return <Text type="secondary">—</Text>;
  return (
    <Space size="small">
      <code style={{ fontSize: 12 }}>{value}</code>
      <Button
        size="small"
        type="text"
        icon={<CopyOutlined />}
        onClick={() => copy(value, label)}
      />
    </Space>
  );
}

function StreamKeyCell({ value }: { value: string }) {
  const [shown, setShown] = useState(false);
  return (
    <Space size="small">
      <code style={{ fontSize: 12 }}>
        {shown ? value : '•'.repeat(Math.min(value.length, 18))}
      </code>
      <Button
        size="small"
        type="text"
        icon={shown ? <EyeInvisibleOutlined /> : <EyeOutlined />}
        onClick={() => setShown((s) => !s)}
      />
      <Button
        size="small"
        type="text"
        icon={<CopyOutlined />}
        onClick={() => copy(value, 'stream key')}
      />
    </Space>
  );
}

export function StreamsScene() {
  const [rows, setRows] = useState<StreamingDestinationRow[]>([]);
  const [events, setEvents] = useState<EventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<StreamingDestinationRow | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm<FormValues>();

  const load = async () => {
    setLoading(true);
    try {
      const [streams, allEvents] = await Promise.all([
        api<StreamingDestinationRow[]>('/api/streams'),
        api<EventRow[]>('/api/events'),
      ]);
      setRows(streams);
      setEvents(allEvents);
    } catch (err) {
      message.error((err as { error?: string }).error ?? 'Failed to load streams');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const eventOptions = useMemo(
    () => [
      { value: null, label: 'Shared (not tied to an event)' },
      ...events.map((e) => ({ value: e.id, label: e.title })),
    ],
    [events],
  );

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ platform: 'youtube', event_id: null });
    setModalOpen(true);
  };

  const openEdit = (row: StreamingDestinationRow) => {
    setEditing(row);
    form.setFieldsValue({
      name: row.name,
      platform: row.platform,
      event_id: row.event_id,
      rtmp_url: row.rtmp_url,
      stream_key: row.stream_key,
      backup_url: row.backup_url,
      notes: row.notes,
    });
    setModalOpen(true);
  };

  const onSave = async (values: FormValues) => {
    try {
      if (editing) {
        await api(`/api/streams/${editing.id}`, {
          method: 'PUT',
          body: JSON.stringify(values),
        });
      } else {
        await api('/api/streams', {
          method: 'POST',
          body: JSON.stringify(values),
        });
      }
      setModalOpen(false);
      await load();
    } catch (err) {
      message.error((err as { error?: string }).error ?? 'Save failed');
    }
  };

  const remove = async (id: string) => {
    try {
      await api(`/api/streams/${id}`, { method: 'DELETE' });
      await load();
    } catch (err) {
      message.error((err as { error?: string }).error ?? 'Delete failed');
    }
  };

  const columns: ColumnsType<StreamingDestinationRow> = [
    {
      title: 'Name',
      dataIndex: 'name',
      render: (name: string, row) => {
        const p = PLATFORMS.find((x) => x.value === row.platform);
        return (
          <div>
            <Text strong>{name}</Text>
            <div>
              <Tag color={p?.color ?? 'default'}>{p?.label ?? row.platform}</Tag>
              {row.event_title ? (
                <Tag>{row.event_title}</Tag>
              ) : row.event_id ? (
                <Tag>Event-attached</Tag>
              ) : (
                <Tag color="default">Shared</Tag>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: 'RTMP URL',
      dataIndex: 'rtmp_url',
      render: (url: string) => <CopyField value={url} label="RTMP URL" />,
    },
    {
      title: 'Stream key',
      dataIndex: 'stream_key',
      render: (k: string) => <StreamKeyCell value={k} />,
    },
    {
      title: 'Backup URL',
      dataIndex: 'backup_url',
      render: (b: string | null) => <CopyField value={b} label="backup URL" />,
    },
    {
      title: '',
      key: 'actions',
      width: 140,
      render: (_t, row) => (
        <Space>
          <Button
            size="small"
            icon={<CloudDownloadOutlined />}
            title="Download as Blackmagic streaming XML"
            onClick={() =>
              void apiDownload(
                `/api/streams/${row.id}/blackmagic.xml`,
                `${row.name}-blackmagic.xml`,
              ).catch((err: { error?: string }) =>
                message.error(err.error ?? 'Download failed'),
              )
            }
          />
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          <Popconfirm
            title="Delete this destination?"
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => remove(row.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <AppLayout>
      <PageHead title="Streaming destinations" />
      <Space
        align="center"
        style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}
      >
        <Title level={2} style={{ margin: 0 }}>
          Streaming destinations
        </Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          New destination
        </Button>
      </Space>
      <Paragraph type="secondary">
        Stream keys + RTMP endpoints for the platforms vMix is publishing to.
        Configs tagged with an event live next to that production; "Shared"
        ones are persistent destinations (e.g. AOTV's own YouTube channel)
        re-used across shows.
      </Paragraph>

      <Card bodyStyle={{ padding: 0 }}>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={rows}
          loading={loading}
          pagination={false}
          locale={{ emptyText: 'No streaming destinations yet.' }}
        />
      </Card>

      <Modal
        title={editing ? `Edit ${editing.name}` : 'New streaming destination'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        okText="Save"
        width={680}
      >
        <Form form={form} layout="vertical" onFinish={onSave}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please give the destination a name' }]}
          >
            <Input placeholder='e.g. "AOTV YouTube" or "Client Facebook page"' autoFocus />
          </Form.Item>
          <Form.Item name="platform" label="Platform" rules={[{ required: true }]}>
            <Select
              options={PLATFORMS.map((p) => ({ value: p.value, label: p.label }))}
            />
          </Form.Item>
          <Form.Item name="event_id" label="Event">
            <Select options={eventOptions} />
          </Form.Item>
          <Form.Item
            name="rtmp_url"
            label="RTMP / RTMPS URL"
            rules={[{ required: true, message: 'The RTMP URL is required' }]}
          >
            <Input placeholder="rtmps://a.rtmp.youtube.com/live2" />
          </Form.Item>
          <Form.Item
            name="stream_key"
            label="Stream key"
            rules={[{ required: true, message: 'Stream key is required' }]}
          >
            <Input.Password placeholder="e.g. xxxx-xxxx-xxxx-xxxx" />
          </Form.Item>
          <Form.Item name="backup_url" label="Backup URL (optional)">
            <Input placeholder="rtmps://b.rtmp.youtube.com/live2?backup=1" />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} />
          </Form.Item>
        </Form>
      </Modal>
    </AppLayout>
  );
}
